import React from 'react';
import { Eye, X, Palette } from 'lucide-react';

/**
 * Sticky banner shown on top of the page when rendering an AI proposal preview.
 * Triggered by `?preview_proposal=<id>` URL param.
 *
 * Allows switching between proposed theme variants (preset / custom / none)
 * via a tiny inline picker — query param ?theme=preset|custom|none.
 */
export default function PreviewBanner({ info }) {
  if (!info) return null;

  const opts = info.theme_options || [];
  const appliedKind = info.applied_theme?.kind || (opts.length ? 'auto' : 'none');

  const switchTheme = (value) => {
    try {
      const url = new URL(window.location.href);
      if (value === 'auto') url.searchParams.delete('theme');
      else url.searchParams.set('theme', value);
      window.location.href = url.toString();
    } catch {
      /* ignore */
    }
  };

  return (
    <div
      data-testid="preview-banner"
      className="sticky top-0 z-[9999] bg-gradient-to-r from-purple-600 to-pink-600 text-white py-2 px-4 shadow-lg"
    >
      <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-3 text-sm">
        <div className="flex items-center gap-2 min-w-0">
          <Eye className="h-4 w-4 shrink-0" />
          <span className="font-medium">Náhled AI návrhu</span>
          <span className="hidden sm:inline opacity-90 truncate">
            • {info.preview_title || info.preview_slug}
          </span>
          <span className="hidden md:inline opacity-75 text-xs">
            (změny nejsou aplikované — pouze náhled)
          </span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {opts.length > 0 && (
            <div className="flex items-center gap-1 bg-white/10 rounded px-2 py-1" data-testid="preview-theme-switcher">
              <Palette className="h-3.5 w-3.5 opacity-80" />
              <span className="text-xs opacity-90 hidden lg:inline">Theme:</span>
              {opts.some((o) => o.kind === 'preset') && (
                <button
                  onClick={() => switchTheme('preset')}
                  className={`text-xs px-1.5 py-0.5 rounded ${appliedKind === 'preset' ? 'bg-white text-purple-700 font-medium' : 'hover:bg-white/10'}`}
                  data-testid="preview-theme-preset"
                >
                  Preset
                </button>
              )}
              {opts.some((o) => o.kind === 'custom') && (
                <button
                  onClick={() => switchTheme('custom')}
                  className={`text-xs px-1.5 py-0.5 rounded ${appliedKind === 'custom' ? 'bg-white text-purple-700 font-medium' : 'hover:bg-white/10'}`}
                  data-testid="preview-theme-custom"
                >
                  Custom
                </button>
              )}
              <button
                onClick={() => switchTheme('none')}
                className={`text-xs px-1.5 py-0.5 rounded ${appliedKind === 'none' ? 'bg-white text-purple-700 font-medium' : 'hover:bg-white/10'}`}
                data-testid="preview-theme-none"
                title="Bez theme (původní vzhled)"
              >
                Bez
              </button>
            </div>
          )}
          <button
            onClick={() => window.close()}
            className="opacity-75 hover:opacity-100 text-xs flex items-center gap-1 px-2 py-1 rounded bg-white/10 hover:bg-white/20"
            title="Zavřít náhled"
            data-testid="preview-close-btn"
          >
            <X className="h-3 w-3" /> Zavřít
          </button>
        </div>
      </div>
    </div>
  );
}
